import {
  Children,
  forwardRef,
  isValidElement,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import ListSubheader from '@mui/material/ListSubheader';
import MuiMenuList, { type MenuListProps } from '@mui/material/MenuList';
import Typography from '../primitives/Typography';
import { mergeSx } from '../utils/mergeSx';
import SearchField from './SearchField';

type CountryMenuItemProps = {
  id?: string;
  children?: ReactNode;
};

function menuItemSearchText(child: ReactElement<CountryMenuItemProps>): string {
  const id = String(child.props.id ?? '');
  const iso = id.replace(/^country-/i, '');
  const kids = Children.toArray(child.props.children);
  const name = isValidElement<CountryMenuItemProps>(kids[1])
    ? String(kids[1].props.children ?? '')
    : '';
  const code = isValidElement<CountryMenuItemProps>(kids[2])
    ? Children.toArray(kids[2].props.children).join('')
    : '';
  return `${name} ${code} ${iso}`.toLowerCase();
}

function filterCountryMenuItems(children: ReactNode, query: string): ReactNode[] {
  const q = query.trim().toLowerCase();
  const items = Children.toArray(children);
  if (!q) return items;
  return items.filter((child) => {
    if (!isValidElement<CountryMenuItemProps>(child)) return true;
    return menuItemSearchText(child).includes(q);
  });
}

/**
 * MenuList slot for `MuiTelInput`: keeps default Menu Paper (anchor/position intact)
 * and filters country items from a sticky search row.
 */
const PhoneCountryMenuList = forwardRef<HTMLUListElement, MenuListProps>(
  function PhoneCountryMenuList({ children, sx, ...props }, ref) {
    const { t } = useTranslation('common');
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => filterCountryMenuItems(children, query), [children, query]);

    return (
      <MuiMenuList ref={ref} {...props} sx={mergeSx({ py: 0 }, sx)}>
        <ListSubheader
          sx={{
            bgcolor: 'background.paper',
            lineHeight: 'normal',
            py: 1,
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <SearchField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('search')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </ListSubheader>
        {filtered.length > 0 ? (
          filtered
        ) : (
          <Typography component="li" variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
            {t('globalSearchNoResults')}
          </Typography>
        )}
      </MuiMenuList>
    );
  },
);

export default PhoneCountryMenuList;
